import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Skeleton, Typography } from '@mui/material';

import SetSelectionListItem, { TestSet } from '../SetSelectionListItem/SetSelectionListItem';
import Snackbar from '/packages/shared-components/modal/Snackbar/Snackbar';
import EmptyState from '/packages/shared-components/EmptyState/EmptyState';

import { TestType, APITestPlan, APITestSetInstance, TestSetSorting } from '~src/generated/portal/graphql';
import { GET_API_TEST_SETS_BOUNDED } from '~/src/queries/codegen/getApiTestSets';
import GET_TEST_PLAN from '~src/queries/codegen/apiTestPlan';

export interface SetSelectionListProps {
  onSetsChange: (sets: TestSet[]) => void;
  testSetTotal: number;
  setTestSetTotal: (total: number) => void;
  testPlanId?: string;
  testSetInstances?: APITestSetInstance[];
  search: string | null;
}

const SetSelectionList = ({
  onSetsChange,
  testSetTotal,
  setTestSetTotal,
  testPlanId,
  search,
}: SetSelectionListProps) => {
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [selectedSets, setSelectedSets] = useState<string[]>([]);
  const { loading, error, data } = useQuery(GET_API_TEST_SETS_BOUNDED, {
    variables: { type: TestType.API, sortBy: TestSetSorting.NameAsc },
  });

  // Get selected test sets for duplicate
  const { data: selectedData, loading: selectedLoading } = useQuery(GET_TEST_PLAN, {
    variables: { id: testPlanId || '' },
    skip: !testPlanId,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (selectedData) {
      setSelectedSets(getSelectedSets(selectedData.apiTestPlan as APITestPlan));
    }
  }, [selectedData]);

  useEffect(() => {
    const getSetQuantity = (id: string) => {
      const quantity = selectedSets.filter((set) => {
        return id == set;
      }).length;
      return quantity;
    };

    const newTestSets = data?.testSets.map((testSet) => ({
      id: testSet.id,
      name: testSet.name,
      description: testSet.description || '',
      quantity: getSetQuantity(testSet.id),
    }));
    setTestSets(newTestSets || []);
    setTestSetTotal(selectedSets.length);
  }, [data, selectedData, selectedSets, setTestSetTotal]);

  useEffect(() => {
    onSetsChange(testSets);
  }, [testSets, onSetsChange]);

  const getSelectedSets = (data?: APITestPlan | null) => {
    const selectedIds = data?.testSetInstances.map((setInstance) => {
      return setInstance.testSetId;
    });
    return selectedIds || [];
  };

  const matchSearch = (test: TestSet, term: string | null) => {
    if (term === undefined || term === '' || term === null) return true;
    return test.name.toLowerCase().includes(term.toLowerCase().trim());
  };

  const filteredTestSets = useMemo(
    () => (search ? testSets.filter((test) => matchSearch(test, search)) || [] : testSets),

    [testSets, search],
  );
  const handleChangeSetQuantityClick = (set: TestSet, type: 'add' | 'remove') => {
    setTestSets((oldTestSets) => {
      const newTestSets = oldTestSets.map((testSet) => {
        const { id, name, description, quantity } = testSet;
        if (set.id === testSet.id) {
          return { id, name, description, quantity: type === 'add' ? quantity + 1 : quantity - 1 };
        }
        return testSet;
      });
      return newTestSets;
    });
    const newTotal = type === 'add' ? testSetTotal + 1 : testSetTotal - 1;
    setTestSetTotal(newTotal);
  };

  return (
    <Box>
      {loading || selectedLoading ? (
        <Skeleton />
      ) : error ? (
        <Snackbar content={error.message} status="error" variant="subtle" />
      ) : testSets.length === 0 ? (
        <p>There are no sets</p>
      ) : (
        <>
          {!loading && !error && filteredTestSets.length === 0 ? (
            <EmptyState sx={{ minHeight: '50vh', width: '100%' }}>
              <Typography variant="subtitle1" color="text.secondary" lineHeight={1.5} align="center">
                No results match your criteria.
              </Typography>
            </EmptyState>
          ) : (
            filteredTestSets.map((set) => (
              <SetSelectionListItem
                key={set.id}
                set={set}
                onAddSetClick={() => handleChangeSetQuantityClick(set, 'add')}
                onRemoveSetClick={() => handleChangeSetQuantityClick(set, 'remove')}
              />
            ))
          )}
        </>
      )}
    </Box>
  );
};

export default SetSelectionList;
